import { Vue } from "@/vue.setup"
import { makeModule } from "@/store/utils"
import { withOnly } from "@/utils/functions"
import { make } from "vuex-pathify"
import { asSequence, generateSequence } from "sequency"


const defaultTotal = 0;
const defaultPerPage = 0;
const defaultPage = 1;
const defaultLastPage = defaultPage;
const minPage = defaultPage;
const isValidPage = (min, max, page) => min <= page && page <= max;

const nameMapping = {
	diameter: "Diamètre",
	pitch: "Pas",
};


const state = {
	norms: [],
	headers: [],

	// Data Driven Design
	loading: [],
	total: [],
	perPage: [],
	page: [],
	lastPage: [],
	pages: [],
};

const getters = {
	...make.getters(state),

	// DDD
	isLoading(state){
		return i => state.loading[i];
	},
	getTotal(state){
		return i => state.total[i];
	},
	getPerPage(state){
		return i => state.perPage[i];
	},
	getPage(state){
		return i => state.page[i];
	},
	getLastPage(state){
		return i => state.lastPage[i];
	},
	getPages(state){
		return i => state.pages[i];
	},

	pageContent(state){
		return i => {
			const page = getters.getPage(state)(i);
			const pages = getters.getPages(state)(i);

			return pages && (page - 1) in pages
			? pages[page - 1]
			: null;
		};
	},
	hasPageContent(state){
		return i => {
			const page = getters.getPage(state)(i);
			const pages = getters.getPages(state)(i);
			const lastPage = getters.getLastPage(state)(i);

			return isValidPage(minPage, lastPage, page)
			&& pages
			&& (page - 1) in pages
			&& Array.isArray(getters.pageContent(state)(i));
		};
	},
	hasLoadedContent(state){
		return i => {
			const total = getters.getTotal(state)(i);
			const page = getters.getPage(state)(i);
			const perPage = getters.getPerPage(state)(i);
			const lastPage = getters.getLastPage(state)(i);

			return /* state.headers.length > 0 || */ (
				total !== defaultTotal
				&& perPage !== defaultPerPage
				&& page !== defaultPage
				&& lastPage !== defaultLastPage
			);
		};
	},
	pageIsValid(state){
		return i => {
			const page = getters.getPage(state)(i);
			const lastPage = getters.getLastPage(state)(i);
			return isValidPage(minPage, lastPage, page);
		}
	},
	hasPreviousPage(state){
		return i => {
			const page = getters.getPage(state)(i);
			return minPage < page;
		};
	},
	hasNextPage(state){
		return i => {
			const page = getters.getPage(state)(i);
			const lastPage = getters.getLastPage(state)(i);
			return page < lastPage;
		};
	},
};

const mutations = {
	...make.mutations(state),
	START_LOADING(state, i){
		Vue.set(state.loading, i, true);
	},
	STOP_LOADING(state, i){
		Vue.set(state.loading, i, false);
	},
	CHANGE_NORMS(state, norms){
		const seqOf = item => {
			return generateSequence(() => {
				if(Array.isArray(item))
					return [...item]; // Not duplicating the array would cause it to be shared

				return item;
			}).take(norms.length)
			.toArray();
		};

		const genAndSet = (item, mutation) => {
			const items = seqOf(item);
			mutations[mutation](state, items);
		};

		genAndSet(defaultTotal, "SET_TOTAL");
		genAndSet(defaultPerPage, "SET_PER_PAGE");
		genAndSet(defaultPage, "SET_PAGE");
		genAndSet(defaultLastPage, "SET_LAST_PAGE");
		genAndSet([], "SET_PAGES");
		genAndSet(false, "SET_LOADING");

		mutations.SET_NORMS(state, norms);
	},

	SET_TOTAL_I(state, { index, total }){
		Vue.set(state.total, index, total);
	},
	SET_PER_PAGE_I(state, { index, perPage }){
		Vue.set(state.perPage, index, perPage);
	},
	SET_LAST_PAGE_I(state, { index, lastPage }){
		Vue.set(state.lastPage, index, lastPage);
	},

	SET_PAGE_I(state, { index, newPage }){
		Vue.set(state.page, index, newPage);
	},
	CHANGE_PAGE(state, { index, newPage }){
		const lastPage = getters.getLastPage(state)(index);

		if(!isValidPage(minPage, lastPage, newPage))
			return;

		mutations.SET_PAGE_I(state, { newPage, index });
	},

	SET_PAGE_CONTENT(state, { index, page, content }){
		const mappedContent = content.map(item => {
			for(const key in item){
				if(key in nameMapping)
					item[nameMapping[key]] = item[key]; //add the mapped key as an alias to the original
			}

			return item;
		});

		Vue.set(state.pages[index], page - 1, mappedContent);
	},
};

const actions = {
	...make.actions(state),
	async LOAD({ commit }, { index, promise }){
		commit("START_LOADING", index);
		try{
			await promise();
		}finally{
			commit("STOP_LOADING", index);
		}
	},
	FETCH_PAGE({ dispatch, commit, getters }, { index, id, vm, page = 1 } = {}){
		return dispatch("LOAD", {
			index,
			async promise(){
				const oldPage = getters.getPage(index);

				commit("CHANGE_PAGE", { index, newPage: page });
				if(!getters.hasPageContent(index)){ // only load content if it's not already loaded
					try {
						const {
							paginated,
							headers,
						} = await vm.$json.get(`/norm-data/${id}/${page}`);

						const {
							total,
							perPage,
							lastPage,
							data,
						} = paginated;

						if(!getters.hasLoadedContent(index)){ // only set "metadata" on very first load
							const mappedHeaders = headers.map(header => {
								return (header in nameMapping)
								? nameMapping[header]
								: header;
							});

							commit("SET_HEADERS", mappedHeaders);
							commit("SET_TOTAL_I", { index, total });
							commit("SET_PER_PAGE_I", { index, perPage });
							commit("SET_LAST_PAGE_I", { index, lastPage });
						}

						if(getters.pageIsValid(index)){
							commit("SET_PAGE_CONTENT", {
								index,
								page,
								content: data,
							});
						}
					} catch (e) {
						commit("CHANGE_PAGE", oldPage);
						const name = getters.norms[index];
						vm.$toast.error(`Nous n'avons pas pu charger la page ${page} pour le contenu "${name}"`);
					}
				}
			},
		});
	},
	LOAD_NEXT_PAGE({ dispatch, getters }, payload){
		const { index } = payload;
		const page = getters.getPage(index);

		return dispatch("FETCH_PAGE", {
			...payload,
			page: page + 1,
		});
	},
	LOAD_PREV_PAGE({ dispatch, getters }, payload){
		const { index } = payload;
		const page = getters.getPage(index);

		return dispatch("FETCH_PAGE", {
			...payload,
			page: page - 1,
		});
	},
};

export default makeModule({
	state,
	getters,
	mutations,
	actions,
})